import { wrap, proxy } from 'comlink';
import { logError } from 'src/utils/logger';
import { getAssetsUrl } from 'src/utils/getBaseUrl';

class PagePropagation {
  #worker;
  #eventHandlers = {};
  #eventList = [
    'pageContentUpdated',
    'settingsUpdated',
    'fetchPageContent',
    'savePageContent'
  ];

  #initialized = false;

  #id;

  on (event, callback) {
    if (this.#eventList.includes(event)) {
      this.#eventHandlers[event] = Array.isArray(this.#eventHandlers[event])
        ? this.#eventHandlers[event].concat(callback)
        : [callback];
    }
  }

  async init () {
    try {
      if (this.#initialized) return;

      // TODO: Fix usage of this to load directly from application url
      const workerUrl = process.env.PAGE_WORKER || getAssetsUrl('page-worker.js');
      const response = await fetch(workerUrl, {
        method: 'GET',
        cache: 'no-cache',
        'content-type': 'application/javascript'
      });

      if (response.ok) {
        const data = await response.text();
        const blobURL = window.URL.createObjectURL(new Blob([data]));
        this.#worker = wrap(new Worker(blobURL));
        await this.#hookCallbacks();
      }

      this.#initialized = true;
    } catch (err) {
      throw new Error('Error initializing Page Propagation', err);
    }
  }

  /**
   * Register all callback handlers here
   */
  async #hookCallbacks () {
    this.#worker.registerPageContentUpdated(proxy(this.#pageContentUpdated.bind(this)));
    this.#worker.registerSettingsUpdated(proxy(this.#settingsUpdated.bind(this)));
    this.#worker.registerFetchPageContent(proxy(this.#fetchPageContent.bind(this)));
    this.#worker.registerSavePageContent(proxy(this.#savePageContent.bind(this)));
    this.#worker.registerFetchProject(proxy(this.#fetchProject.bind(this)));
    this.#worker.registerSaveProject(proxy(this.#saveProject.bind(this)));
    this.#worker.registerGetAllPages(proxy(this.#getAllPages.bind(this)));
  }

  async initializeSettings (metadata) {
    try {
      this.#id = metadata.id;

      const workerSettings = {
        debug: false || INKIVE.C.DEBUG
      };
      const settings = await this.#worker.initializeSettings(metadata, workerSettings);

      this.#settingsUpdated(settings);
    } catch (error) {
      logError('initializeSettings', error);
    }
  }

  async setRange (metadata, pages) {
    try {
      const currentPageId = this.#getCurrentPageId();

      await this.#worker.setRangeEntry({
        id: this.#id,
        currentPageId,
        metadata,
        pages
      });
    } catch (error) {
      logError('setRange', error);
    }
  }

  #pageContentUpdated (args) {
    this.#eventHandlers.pageContentUpdated?.forEach(handler => handler(args));
  }

  #settingsUpdated (args) {
    this.#eventHandlers.settingsUpdated?.forEach(handler => handler(args));
  }

  #getCurrentPageId () {
    return INKIVE.PageFactory.getCurrentPageId();
  }

  /**
   * Handle fetching of page content for webworker as we use cookies
   * @param { pageId } args
   * @returns { pageId, content }
   */
  async #fetchPageContent ({ pageId }) {
    if (!pageId) return;

    if (INKIVE.PageFactory.isCurrentPage(pageId)) {
      return INKIVE.PageFactory.getCurrentPageData();
    }

    return await INKIVE.PageFactory.getPage(pageId);
  }

  async #savePageContent ({ pageId, content }) {
    return await INKIVE.PageFactory.savePageState(pageId, content);
  }

  async #fetchProject () {
    return await INKIVE.PageFactory.getProjectData();
  }

  async #saveProject (data) {
    try {
      return await INKIVE.PageFactory.saveProjectState(data);
    } catch (error) {
      logError('SaveProject', error);
    }
  }

  async #getAllPages () {
    const pages = INKIVE.ProjectFacade.get('designs').map(page => ({
      id: page.id,
      pageNumber: page.page_number
    }));

    return { pages };
  }

  async propagateContent ({ pageId }) {
    try {
      await this.#worker.propagateContent({ pageId });
    } catch (error) {
      logError('propagateContent', error);
    }
  }

  async createPage (newPage, options = {}) {
    try {
      const currentPageId = this.#getCurrentPageId();
      const { pages: allPages } = await this.#getAllPages();
      await this.#worker.createPage({ newPage, allPages, currentPageId, options });
    } catch (error) {
      logError('createPage', error);
    }
  }

  async duplicatePage (duplicatedPage) {
    try {
      const { pages: allPages } = await this.#getAllPages();
      await this.#worker.duplicatePage({ duplicatedPage, allPages });
    } catch (error) {
      logError('duplicatePage', error);
    }
  }

  async deletePage (args) {
    try {
      const { pages: allPages } = await this.#getAllPages();
      await this.#worker.deletePage({
        deletedPage: args,
        allPages
      });
    } catch (error) {
      logError('deletePage', error);
    }
  }

  async orderChanged (args) {
    try {
      const allPages = args.previousOrder;
      await this.#worker.orderChanged({
        ...args,
        allPages
      });
    } catch (error) {
      logError('orderChanged', error);
    }
  }

  async extractTextElements ({ id, content }) {
    try {
      return await this.#worker.extractTextElementsFromPage({ id, content });
    } catch (error) {
      logError('extractTextElements', error);
    }
  }
}

module.exports = {
  pagePropagationInstance: new PagePropagation()
};
